import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Shared dependent Brand -> Model dropdown, reading the single Vehicle Brand / Model masters.
// Relationship is by brand _id (not text). Stores selected names via onChange(brand, model).
export function BrandModelSelect({ brand, model, onChange, size = "h-9", labels = true, testidPrefix = "vm" }) {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/master/vehicle_brand", { params: { active: "true" } }).then((r) => r.data).catch(() => []),
      api.get("/master/vehicle_model", { params: { active: "true" } }).then((r) => r.data).catch(() => []),
    ]).then(([b, m]) => { setBrands(b || []); setModels(m || []); });
  }, []);

  const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");
  const selectedBrand = useMemo(
    () => brands.find((b) => norm(b.brand_name) === norm(brand)),
    [brands, brand]
  );
  const brandModels = useMemo(
    () => (selectedBrand ? models.filter((m) => m.brand_id === selectedBrand.id) : []),
    [models, selectedBrand]
  );
  const selectedModel = useMemo(
    () => brandModels.find((m) => norm(m.model) === norm(model)),
    [brandModels, model]
  );
  // Canonical values so Radix Select always matches an option (handles legacy punctuation drift).
  const brandValue = selectedBrand ? selectedBrand.brand_name : (brand || "");
  const modelValue = selectedModel ? selectedModel.model : (model || "");

  const brandOptions = useMemo(() => {
    const list = brands.map((b) => b.brand_name).filter(Boolean);
    if (brand && !list.some((x) => norm(x) === norm(brand))) list.unshift(brand);
    return list;
  }, [brands, brand]);
  const modelOptions = useMemo(() => {
    const list = brandModels.map((m) => m.model).filter(Boolean);
    if (model && !list.some((x) => norm(x) === norm(model))) list.unshift(model);
    return list;
  }, [brandModels, model]);

  return (
    <>
      <div>
        {labels && <Label className="text-xs">Merk</Label>}
        <Select value={brandValue} onValueChange={(v) => onChange(v, "")}>
          <SelectTrigger data-testid={`${testidPrefix}-brand`} className={`mt-1 ${size}`}><SelectValue placeholder="Pilih merk…" /></SelectTrigger>
          <SelectContent>{brandOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        {labels && <Label className="text-xs">Model / Type</Label>}
        <Select value={modelValue} onValueChange={(v) => onChange(brandValue, v)} disabled={!brandValue}>
          <SelectTrigger data-testid={`${testidPrefix}-model`} className={`mt-1 ${size}`}><SelectValue placeholder={brandValue ? "Pilih model…" : "Pilih merk dulu"} /></SelectTrigger>
          <SelectContent>{modelOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </>
  );
}
